import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  // Convert number to string and handle decimals
  const numStr = Math.abs(amount).toFixed(0);
  
  // Split integer into parts based on Indian numbering system
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNums = numStr.substring(0, numStr.length - 3);
  
  // Add commas according to Indian number system
  const formatted = otherNums !== '' 
    ? otherNums.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return '₹' + (amount < 0 ? '-' : '') + formatted;
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const InflationCalculator = () => {
  const [values, setValues] = useState({
    currentAmount: 100000,
    inflationRate: 6,
    timePeriod: 10,
    currentIncome: 50000,
    currentExpenses: 40000
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showToolTip, setShowToolTip] = useState('');
  
  const handleChange = (key, value) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateInflation = () => {
    const { 
      currentAmount, 
      inflationRate, 
      timePeriod,
      currentIncome,
      currentExpenses
    } = values;
    
    // Calculate future value of money with compound inflation
    // FV = PV * (1 + inflation)^years
    const futureAmount = currentAmount * Math.pow(1 + (inflationRate / 100), timePeriod);
    
    // Calculate the purchasing power loss
    const purchasingPowerLoss = currentAmount - (currentAmount * Math.pow(1 - (inflationRate / 100), timePeriod));
    
    // Calculate percentage loss in purchasing power
    const purchasingPowerLossPercentage = (purchasingPowerLoss / currentAmount) * 100;
    
    // Calculate required future income to maintain same standard of living
    const requiredFutureIncome = currentIncome * Math.pow(1 + (inflationRate / 100), timePeriod);
    
    // Calculate future expenses
    const futureExpenses = currentExpenses * Math.pow(1 + (inflationRate / 100), timePeriod);
    
    // Calculate savings erosion
    // If current savings are invested at 0% return
    const savingsErosion = (currentAmount - (currentAmount / Math.pow(1 + (inflationRate / 100), timePeriod))) / currentAmount * 100;
    
    // Calculate inflation-adjusted return
    // If you need to calculate a real return given a nominal interest rate
    // For example, if investment return is 8% and inflation is 6%
    const nominalReturnRate = 8; // Assuming an 8% investment return
    const realReturnRate = ((1 + (nominalReturnRate / 100)) / (1 + (inflationRate / 100)) - 1) * 100;
    
    // Calculate investment needed to maintain purchasing power
    // Assuming the investment gives a return equal to nominalReturnRate
    const requiredInvestment = currentAmount * (Math.pow(1 + (inflationRate / 100), timePeriod) / Math.pow(1 + (nominalReturnRate / 100), timePeriod));
    
    return {
      futureAmount: Math.round(futureAmount),
      purchasingPowerLoss: Math.round(purchasingPowerLoss),
      purchasingPowerLossPercentage: Math.round(purchasingPowerLossPercentage * 100) / 100,
      requiredFutureIncome: Math.round(requiredFutureIncome),
      futureExpenses: Math.round(futureExpenses),
      savingsErosion: Math.round(savingsErosion * 100) / 100,
      realReturnRate: Math.round(realReturnRate * 100) / 100,
      requiredInvestment: Math.round(requiredInvestment)
    };
  };

  const [result, setResult] = useState(calculateInflation());

  useEffect(() => {
    setResult(calculateInflation());
  }, [values]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Inflation Calculator</h1>
        <h2 className="text-lg text-gray-600">Calculate the impact of inflation on your money over time</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Inflation Inputs */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Basic Inflation Details</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('currentAmount')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'currentAmount' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Present value of money you want to calculate inflation impact for
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.currentAmount}
                    onChange={(e) => handleChange('currentAmount', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1000}
                    max={10000000}
                    step={1000}
                    value={values.currentAmount}
                    onChange={(e) => handleChange('currentAmount', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1K</span>
                    <span>₹1Cr</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Annual Inflation Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('inflationRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'inflationRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Estimated annual inflation rate (typically 4-7% in India)
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.inflationRate}
                    onChange={(e) => handleChange('inflationRate', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={0.1}
                    value={values.inflationRate}
                    onChange={(e) => handleChange('inflationRate', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1%</span>
                    <span>20%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Time Period (Years)</label>
                  <input
                    type="number"
                    value={values.timePeriod}
                    onChange={(e) => handleChange('timePeriod', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={50}
                    step={1}
                    value={values.timePeriod}
                    onChange={(e) => handleChange('timePeriod', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>50 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Current Monthly Income
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('currentIncome')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'currentIncome' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Your current monthly income
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.currentIncome}
                        onChange={(e) => handleChange('currentIncome', Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={10000}
                        max={1000000}
                        step={5000}
                        value={values.currentIncome}
                        onChange={(e) => handleChange('currentIncome', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹10K</span>
                        <span>₹10L</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Current Monthly Expenses
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('currentExpenses')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'currentExpenses' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Your current monthly expenses
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.currentExpenses}
                        onChange={(e) => handleChange('currentExpenses', Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={5000}
                        max={500000}
                        step={5000}
                        value={values.currentExpenses}
                        onChange={(e) => handleChange('currentExpenses', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹5K</span>
                        <span>₹5L</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Inflation Impact</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.futureAmount)}
                  </div>
                  <div className="text-sm text-gray-300">Future equivalent value</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Purchasing Power Loss</span>
                    <span className="font-bold">{formatCurrency(result.purchasingPowerLoss)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Loss in Purchasing Power</span>
                    <span className="font-bold">{result.purchasingPowerLossPercentage}%</span>
                  </div>

                  {showAdvanced && (
                    <>
                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Required Future Income</span>
                        <span className="font-bold">{formatCurrency(result.requiredFutureIncome)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Future Monthly Expenses</span>
                        <span className="font-bold">{formatCurrency(result.futureExpenses)}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Savings Erosion</span>
                    <span className="font-bold">{result.savingsErosion}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Real Return Rate (at 8% nominal)</span>
                    <span className="font-bold">{result.realReturnRate}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Required Investment at 8%</span>
                    <span className="font-bold">{formatCurrency(result.requiredInvestment)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Start Inflation-Proof Investing →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InflationCalculator;